import { z } from 'zod';

import { UnitSystem } from '@optical/utils';

import { isoDatetimeToDate } from '../../codecs';
import { Position, playerGroupSchema, playerSchema } from '../../roster';
import { SessionType, phaseTypeSchema } from '../../sessions';
import { Locale, thresholdsSchema } from '../../settings';
import { ShotLoad } from '../../statistics';
import { CourtArea } from '../court-area';
import { playerReportScopeSchema } from './player-report-data-query';

// ******************* Total Data *******************

export type PlayerReportAreaShotStats = z.output<typeof playerReportAreaShotStatsSchema>;
export type PlayerReportAreaShotStatsDTO = z.input<typeof playerReportAreaShotStatsSchema>;

export const playerReportAreaShotStatsSchema = z.object({
  fga: z.number(),
  fgm: z.number(),
  fgp: z.number(),
  distr: z.number(),
  xvalue: z.number(),
});

export type PlayerReportAreaShotLoadStats = z.output<typeof playerReportAreaShotLoadStatsSchema>;
export type PlayerReportAreaShotLoadStatsDTO = z.input<typeof playerReportAreaShotLoadStatsSchema>;

export const playerReportAreaShotLoadStatsSchema = z.object({
  fga: z.number(),
  fgm: z.number(),
  fgp: z.number(),
  distr: z.number(),
  xvalue: z.number(),
});

export type PlayerReportShotStats = z.output<typeof playerReportShotStatsSchema>;
export type PlayerReportShotStatsDTO = z.input<typeof playerReportShotStatsSchema>;

export const playerReportShotStatsSchema = z.object({
  fga: z.number(),
  fgm: z.number(),
  fgp: z.number(),
  twopa: z.number(),
  twopm: z.number(),
  twopp: z.number(),
  threepa: z.number(),
  threepm: z.number(),
  threepp: z.number(),
  fta: z.number(),
  ftm: z.number(),
  ftp: z.number(),
  efgp: z.number(),
});

export type PlayerReportShotLoadStats = z.output<typeof playerReportShotLoadStatsSchema>;
export type PlayerReportShotLoadStatsDTO = z.input<typeof playerReportShotLoadStatsSchema>;

export const playerReportShotLoadStatsSchema = z.object({
  fga: z.number(),
  fgm: z.number(),
  fgp: z.number(),
  distr: z.number(),
});

export type PlayerReportPerformanceStats = z.output<typeof playerReportPerformanceStatsSchema>;
export type PlayerReportPerformanceStatsDTO = z.input<typeof playerReportPerformanceStatsSchema>;

export const playerReportPerformanceStatsSchema = z.object({
  dist: z.number(),
  distpm: z.number(),
  load: z.number(),
  loadpm: z.number(),
  sprints: z.number(),
  sprintspm: z.number(),
  time: z.number(),
});

export type PlayerReportTotalData = z.output<typeof playerReportTotalDataSchema>;
export type PlayerReportTotalDataDTO = z.input<typeof playerReportTotalDataSchema>;

export const playerReportTotalDataSchema = z.object({
  areaShotStats: z.record(z.enum(CourtArea), playerReportAreaShotStatsSchema),
  areaShotLoadStats: z.record(
    z.enum(CourtArea),
    z.record(z.enum(ShotLoad), playerReportAreaShotLoadStatsSchema),
  ),
  performanceStats: z.union([playerReportPerformanceStatsSchema, z.literal(false)]),
  shotLoadStats: z.record(z.enum(ShotLoad), playerReportShotLoadStatsSchema),
  shotStats: playerReportShotStatsSchema,
});

// ******************* Time Series Data *******************

export type TimeSeriesStats = z.output<typeof timeSeriesStatsSchema>;
export type TimeSeriesStatsDTO = z.input<typeof timeSeriesStatsSchema>;

export const timeSeriesStatsSchema = z.object({
  avg: z.number(),
  min: z.number(),
  max: z.number(),
  overall: z.number(),
  std: z.number(),
  values: z.array(z.number().nullable()),
});

export type PlayerReportAreaShotStatsTimeSeries = z.output<
  typeof playerReportAreaShotStatsTimeSeriesSchema
>;
export type PlayerReportAreaShotStatsTimeSeriesDTO = z.input<
  typeof playerReportAreaShotStatsTimeSeriesSchema
>;

export const playerReportAreaShotStatsTimeSeriesSchema = z.object({
  fga: timeSeriesStatsSchema,
  fgm: timeSeriesStatsSchema,
  fgp: timeSeriesStatsSchema,
  distr: timeSeriesStatsSchema,
  xvalue: timeSeriesStatsSchema,
});

export type PlayerReportShotLoadStatsTimeSeries = z.output<
  typeof playerReportShotLoadStatsTimeSeriesSchema
>;
export type PlayerReportShotLoadStatsTimeSeriesDTO = z.input<
  typeof playerReportShotLoadStatsTimeSeriesSchema
>;

export const playerReportShotLoadStatsTimeSeriesSchema = z.object({
  fga: timeSeriesStatsSchema,
  fgm: timeSeriesStatsSchema,
  fgp: timeSeriesStatsSchema,
  distr: timeSeriesStatsSchema,
});

export type PlayerReportShotStatsTimeSeries = z.output<
  typeof playerReportShotStatsTimeSeriesSchema
>;
export type PlayerReportShotStatsTimeSeriesDTO = z.input<
  typeof playerReportShotStatsTimeSeriesSchema
>;

export const playerReportShotStatsTimeSeriesSchema = z.object({
  fga: timeSeriesStatsSchema,
  fgm: timeSeriesStatsSchema,
  fgp: timeSeriesStatsSchema,
  twopa: timeSeriesStatsSchema,
  twopm: timeSeriesStatsSchema,
  twopp: timeSeriesStatsSchema,
  threepa: timeSeriesStatsSchema,
  threepm: timeSeriesStatsSchema,
  threepp: timeSeriesStatsSchema,
  fta: timeSeriesStatsSchema,
  ftm: timeSeriesStatsSchema,
  ftp: timeSeriesStatsSchema,
  efgp: timeSeriesStatsSchema,
});

export type PlayerReportPerformanceStatsTimeSeries = z.output<
  typeof playerReportPerformanceStatsTimeSeriesSchema
>;
export type PlayerReportPerformanceStatsTimeSeriesDTO = z.input<
  typeof playerReportPerformanceStatsTimeSeriesSchema
>;

export const playerReportPerformanceStatsTimeSeriesSchema = z.object({
  dist: timeSeriesStatsSchema,
  distpm: timeSeriesStatsSchema,
  load: timeSeriesStatsSchema,
  loadpm: timeSeriesStatsSchema,
  sprints: timeSeriesStatsSchema,
  sprintspm: timeSeriesStatsSchema,
  time: timeSeriesStatsSchema,
});

export type PlayerReportTimeSeriesData = z.output<typeof playerReportTimeSeriesDataSchema>;
export type PlayerReportTimeSeriesDataDTO = z.input<typeof playerReportTimeSeriesDataSchema>;

export const playerReportTimeSeriesDataSchema = z.object({
  areaShotStats: z.record(z.enum(CourtArea), playerReportAreaShotStatsTimeSeriesSchema),
  performanceStats: z.union([playerReportPerformanceStatsTimeSeriesSchema, z.literal(false)]),
  shotLoadStats: z.record(z.enum(ShotLoad), playerReportShotLoadStatsTimeSeriesSchema),
  shotStats: playerReportShotStatsTimeSeriesSchema,
  timeBuckets: z.array(z.number()),
});

// ******************* Selection *******************

export type PlayerReportPlayerContext = z.output<typeof playerReportPlayerContextSchema>;
export type PlayerReportPlayerContextDTO = z.input<typeof playerReportPlayerContextSchema>;

export const playerReportPlayerContextSchema = z.object({
  type: z.literal('player'),
  id: z.uuid(),
  player: playerSchema,
});

export type PlayerReportGroupContext = z.output<typeof playerReportGroupContextSchema>;
export type PlayerReportGroupContextDTO = z.input<typeof playerReportGroupContextSchema>;

export const playerReportGroupContextSchema = z.object({
  type: z.literal('group'),
  id: z.uuid(),
  group: playerGroupSchema,
});

export type PlayerReportTeamContext = z.output<typeof playerReportTeamContextSchema>;
export type PlayerReportTeamContextDTO = z.input<typeof playerReportTeamContextSchema>;

export const playerReportTeamContextSchema = z.object({
  type: z.literal('team'),
});

export type PlayerReportPositionContext = z.output<typeof playerReportPositionContextSchema>;
export type PlayerReportPositionContextDTO = z.input<typeof playerReportPositionContextSchema>;

export const playerReportPositionContextSchema = z.object({
  type: z.literal('position'),
  position: z.enum(Position),
});

export type PlayerReportContext = z.output<typeof playerReportContextSchema>;
export type PlayerReportContextDTO = z.input<typeof playerReportContextSchema>;

export const playerReportContextSchema = z.discriminatedUnion('type', [
  playerReportPlayerContextSchema,
  playerReportGroupContextSchema,
  playerReportTeamContextSchema,
  playerReportPositionContextSchema,
]);

export type PlayerReportContextSelection = z.output<typeof playerReportContextSelectionSchema>;
export type PlayerReportContextSelectionDTO = z.input<typeof playerReportContextSelectionSchema>;

export const playerReportContextSelectionSchema = z.object({
  context: playerReportContextSchema.optional(),
});

export type PlayerReportPlayerSelection = z.output<typeof playerReportPlayerSelectionSchema>;
export type PlayerReportPlayerSelectionDTO = z.input<typeof playerReportPlayerSelectionSchema>;

export const playerReportPlayerSelectionSchema = z.object({
  player: playerSchema,
});

export type PlayerReportTimeRangeSelection = z.output<typeof playerReportTimeRangeSelectionSchema>;
export type PlayerReportTimeRangeSelectionDTO = z.input<
  typeof playerReportTimeRangeSelectionSchema
>;

export const playerReportTimeRangeSelectionSchema = z.object({
  granularity: z.enum(['day', 'week']),
  from: isoDatetimeToDate,
  to: isoDatetimeToDate,
  scope: playerReportScopeSchema,
});

export type PlayerReportSessionTypeSelection = z.output<
  typeof playerReportSessionTypeSelectionSchema
>;
export type PlayerReportSessionTypeSelectionDTO = z.input<
  typeof playerReportSessionTypeSelectionSchema
>;

export const playerReportSessionTypeSelectionSchema = z.object({
  sessionTypes: z.array(z.enum(SessionType)),
});

export type PlayerReportPhaseTypeSelection = z.output<typeof playerReportPhaseTypeSelectionSchema>;
export type PlayerReportPhaseTypeSelectionDTO = z.input<
  typeof playerReportPhaseTypeSelectionSchema
>;

export const playerReportPhaseTypeSelectionSchema = z.object({
  phaseTypes: z.array(phaseTypeSchema),
});

export type PlayerReportSelection = z.output<typeof playerReportSelectionSchema>;
export type PlayerReportSelectionDTO = z.input<typeof playerReportSelectionSchema>;

export const playerReportSelectionSchema = z.object({
  contextSelection: playerReportContextSelectionSchema.optional(),
  playerSelection: playerReportPlayerSelectionSchema,
  timeRangeSelection: playerReportTimeRangeSelectionSchema,
  phaseTypeSelection: playerReportPhaseTypeSelectionSchema,
  sessionTypeSelection: playerReportSessionTypeSelectionSchema,
});

// ******************* Settings *******************

export type PlayerReportSettings = z.output<typeof playerReportSettingsSchema>;
export type PlayerReportSettingsDTO = z.input<typeof playerReportSettingsSchema>;

export const playerReportSettingsSchema = z.object({
  locale: z.enum(Locale),
  thresholds: thresholdsSchema,
  unitSystem: z.enum(UnitSystem),
});

// ******************* Data *******************

export type PlayerReportData = z.output<typeof playerReportDataSchema>;
export type PlayerReportDataDTO = z.input<typeof playerReportDataSchema>;

export const playerReportDataSchema = z.object({
  // Report data
  totalData: playerReportTotalDataSchema,
  timeSeriesData: playerReportTimeSeriesDataSchema,

  // Optional comparison context data
  context: z
    .object({
      totalData: playerReportTotalDataSchema,
      timeSeriesData: playerReportTimeSeriesDataSchema,
    })
    .nullable(),

  // Metadata
  selection: playerReportSelectionSchema,
  settings: playerReportSettingsSchema,
});
